import { ErrorResponse, ErrorCode, ErrorSeverity } from '../types/error';
import Logger from './logger';

class GlobalErrorHandler {
  private errorHandler?: (error: Error, context?: any) => void;
  private isInitialized = false;

  init(errorHandler?: (error: Error, context?: any) => void) {
    if (this.isInitialized) {
      Logger.warn('Global error handler initialization', 'Global error handler already initialized');
      return;
    }

    this.errorHandler = errorHandler;
    this.setupGlobalHandlers();
    this.isInitialized = true;
    Logger.info('Global error handler initialization', 'Global error handlers registered successfully');
  }

  private setupGlobalHandlers() {
    window.addEventListener('unhandledrejection', (event) => {
      const error = new Error(`Unhandled Promise Rejection: ${event.reason}`);
      this.handleError(error, {
        type: 'unhandledRejection',
        reason: event.reason,
        promise: event.promise
      });
    });

    window.addEventListener('error', (event) => {
      const error = event.error || new Error(event.message);
      this.handleError(error, {
        type: 'globalError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        message: event.message
      });
    });

    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const error = new Error(`Resource loading failed: ${(event.target as any)?.src || 'unknown resource'}`);
        this.handleError(error, {
          type: 'resourceError',
          target: event.target,
          src: (event.target as any)?.src
        });
      }
    }, true);

    window.addEventListener('online', () => {
      Logger.info('Network connection', 'Network connection restored');
    });

    window.addEventListener('offline', () => {
      const error = new Error('Network connection lost');
      this.handleError(error, {
        type: 'networkOffline'
      });
    });
  }

  private handleError(error: Error, context?: any) {
    const logContext = {
      component: context?.component || 'Global',
      action: context?.type || 'Error',
      path: window.location.pathname,
      url: window.location.href,
    };

    if (context?.filename) {
      (logContext as any).filename = context.filename;
    }
    if (context?.lineno) {
      (logContext as any).lineno = context.lineno;
    }
    if (context?.colno) {
      (logContext as any).colno = context.colno;
    }
    if (context?.src) {
      (logContext as any).resourceUrl = context.src;
    }

    const additionalFields: Record<string, any> = {
      errorType: context?.type || 'unknown',
    };

    if (context?.reason) {
      additionalFields.reason = context.reason;
    }

    if (context?.type === 'resourceError') {
      Logger.warn('Global error - Resource loading failed', error.message, logContext, additionalFields);
    } else if (context?.type === 'networkOffline') {
      Logger.warn('Global error - Network offline', error.message, logContext, additionalFields);
    } else {
      Logger.error('Global error', error.message, logContext, error, additionalFields);
    }

    if (this.errorHandler) {
      this.errorHandler(error, context);
    }

    this.showUserNotification(error, context);
  }

  private getSeverityFromError(error: Error): ErrorSeverity {
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return ErrorSeverity.MEDIUM;
    }
    
    if (error.message.includes('Unhandled Promise Rejection')) {
      return ErrorSeverity.HIGH;
    }
    
    if (error.message.includes('Resource loading failed')) {
      return ErrorSeverity.LOW;
    }
    
    return ErrorSeverity.MEDIUM;
  }

  private showUserNotification(error: Error, context?: any) {
    const shouldNotify = this.shouldNotifyUser(error, context);
    
    if (!shouldNotify) return;

    const message = this.getUserFriendlyMessage(error, context);
    
    Logger.info('User notification', message, {
      component: 'ErrorHandler',
      action: 'showNotification',
    });
  }

  private shouldNotifyUser(error: Error, context?: any): boolean {
    if (context?.type === 'resourceError') {
      return false;
    }
    
    if (context?.type === 'networkOffline') {
      return false;
    }
    
    return context?.type === 'unhandledRejection' || context?.type === 'globalError';
  }

  private getUserFriendlyMessage(error: Error, context?: any): string {
    if (context?.type === 'unhandledRejection') {
      return 'An unexpected error occurred. Please refresh the page if the problem persists.';
    }
    
    if (context?.type === 'globalError') {
      return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
    }
    
    return 'An unexpected error occurred. Please try again.';
  }

  createErrorResponse(error: Error, context?: any): ErrorResponse {
    return {
      status: 500,
      error: ErrorCode.INTERNAL_SERVER_ERROR,
      message: this.getUserFriendlyMessage(error, context),
      details: process.env.NODE_ENV === 'development' ? error.stack : undefined,
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      metadata: context
    };
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

const globalErrorHandler = new GlobalErrorHandler();

export default globalErrorHandler;
export { GlobalErrorHandler };
